import { PAGES, type PageName } from "constants/Pages";
import type { LoadedDraft, Post } from "shared/types/post";
import { extractCorePost } from "utils/posts";
import {
  resolveStage,
  STAGE_STYLES,
  type Stage,
} from "pages/approvals/board/components/post-stage";

const STATES_BY_PAGE: Partial<Record<PageName, Stage[]>> = {
  [PAGES.UPCOMING]: ["scheduled", "queued"],
  [PAGES.ARCHIVE]: ["published", "cancelled", "failed"],
};

export const getPublishingOptions = (page?: PageName) =>
  (page ? (STATES_BY_PAGE[page] ?? []) : []).map((value) => ({
    value,
    label: STAGE_STYLES[value].label,
  }));

export function sanitizePublishingStates(
  values: unknown,
  page?: PageName
): Stage[] {
  if (!Array.isArray(values)) return [];
  return getPublishingOptions(page)
    .map(({ value }) => value)
    .filter((value) => values.includes(value));
}

/** Keep the filter and table pill on the same page-aware publishing model. */
export function normalizePublishingPost(
  post: Post | LoadedDraft,
  page: PageName
): Post {
  const core = extractCorePost(post);
  return {
    ...core,
    postType: page === PAGES.DRAFT ? "draft" : core.postType,
    // Legacy archive records can predate error flags. Without explicit success
    // or cancellation, their terminal publishing outcome is Failed.
    isPotentialError:
      page === PAGES.ARCHIVE &&
      !core.isSuccess &&
      !(core as Post & { isCanceled?: boolean }).isCanceled
        ? true
        : core.isPotentialError,
  };
}

export const resolvePublishingState = (
  post: Post | LoadedDraft,
  page: PageName
): Stage => resolveStage(normalizePublishingPost(post, page));
